import { Component, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy, Input, EventEmitter, Output, OnDestroy } from '@angular/core';
import { FormBuilder, FormArray, FormControl, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { BaseInputComponent } from '../base-input/base-input.component';
import { takeUntil } from 'rxjs';
import { isEqual } from 'lodash';
import { LocaleDto } from '../../../../../../../api/src/creative-unit-package/models/locale.dto';

@Component({
	selector: 'app-multi-add-input',
	templateUrl: './multi-add-input.component.html',
	styleUrls: ['./multi-add-input.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiAddInputComponent extends BaseInputComponent implements OnInit, OnChanges, OnDestroy {
	@Input() addLabel: string = $localize`:Form Button@@multiAddInputAddNewRowButtonLabel:Add New Row`;
	@Input() keyLabel: string = $localize`:Form Label@@multiAddInputKeyLabel:Key`;
	@Input() keyProperty: string = 'key';
	@Input() valueLabel: string = $localize`:Form Label@@multiAddInputValueLabel:Value`;
	@Input() valueProperty: string = 'value';
	@Input() noDataMessage: string = $localize`:No data text@@multiAddInputNoDataText:No data`;
	@Input() selection: any[] | null = null;
	@Input() showNoDataMessage: boolean = false;
	@Input() showHeader: boolean = true;
	@Input() showAddButton: boolean = true;
	@Input() showRemoveButton: boolean = true;
	@Input() lockKey: boolean = false;
	@Input() showLocale: boolean = false;
	@Input() localeData: LocaleDto;
	@Input() keyOptions: { label: string; value: string }[];
	@Input() keyOptionsRequired: boolean = false;
	@Input() dataType: 'array' | 'object' | 'delimited' = 'array';
	@Input() delimiter: string = ',';
	@Input() valueType: 'string' | 'color' = 'string';
	@Input() compact: boolean = false;
	@Input() showAiSuggestions: boolean = false;
	@Input() aiCustomPrompt: string = '';
	@Output() localeChange = new EventEmitter<LocaleDto>();

	public skipChangeDetection: boolean = false;
	public changeSubscription: any;
	public currentValueJson: string = '{}';

	private fuzzyIncludes(haystack: string, needle: string): boolean {
		if (!needle) return true;
		const h = (haystack || '').toLowerCase();
		const n = needle.toLowerCase();
		// Prefix
		if (h.startsWith(n)) return true;
		// Substring
		if (h.includes(n)) return true;
		// Subsequence (characters in order)
		let i = 0;
		for (const c of h) {
			if (c === n[i]) i++;
			if (i === n.length) return true;
		}
		return false;
	}

	getFilteredKeyOptions(index: number): { label: string; value: string }[] {
		if (!this.keyOptions?.length) return [];
		const control = (this.form.get('items') as FormArray)?.at(index)?.get('key') as FormControl;
		const query = (control?.value || '').toString();
		if (!query) return this.keyOptions;
		// Score: prefix > substring > subsequence
		const q = query.toLowerCase();
		const scored = this.keyOptions.map(opt => {
			const v = (opt.value || '').toLowerCase();
			let score = 0;
			if (v.startsWith(q)) score = 300 - v.indexOf(q);
			else if (v.includes(q)) score = 200 - v.indexOf(q);
			else if (this.fuzzyIncludes(v, q)) score = 100;
			return { opt, score };
		}).filter(x => x.score > 0);

		return scored
			.sort((a, b) => b.score - a.score || a.opt.value.localeCompare(b.opt.value))
			.slice(0, 50)
			.map(x => x.opt);
	}

	constructor(private fb: FormBuilder) {
		super();
	}

	ngOnInit(): void {
		super.ngOnInit();
		this.onFormValueChanges();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.currentValue && !changes.currentValue.firstChange) {
			// console.log('changes.currentValue', changes.currentValue?.currentValue, this.currentValue);
			if (this.skipChangeDetection) {
				// console.log('this.skipChangeDetection', this.skipChangeDetection);
				this.skipChangeDetection = false;
				return;
			}

			if (this.changeSubscription) {
				this.changeSubscription.unsubscribe();
			}

			this.initializeForm();
			this.onFormValueChanges();
		}
	}

	initializeForm(currentValue?: any, validators?: ValidatorFn | ValidatorFn[]) {
		if (!this.form) {
			this.form = this.fb.group({
				items: this.fb.array([])
			});
		}

		let value = currentValue || this.currentValue || this.parentForm?.get(this.controlName).value;

		if (value) {
			if (this.dataType === 'array' && Array.isArray(value)) {
				this.setArrayInput(value);
				if (!value.length) {
					this.addNewRow();
				}
			} else if (this.dataType === 'object' && typeof value === 'object' && value !== null) {
				this.setObjectInput(value);
				if (!Object.keys(value).length) {
					this.addNewRow();
				}
			} else if (this.dataType === 'delimited' && typeof value === 'string') {
				this.setDelimitedInput(value);
			}
			// Initialize JSON for AI suggestions
			this.currentValueJson = JSON.stringify(value, null, 2);
		} else {
			this.clearForm(true);
			this.addNewRow();
			this.currentValueJson = '{}';
		}
	}

	handleCurrentValueChange(value: any): void {
		this.initializeForm();
	}

	addNewRow(): void {
		const items = this.form.get('items') as FormArray;
		if (this.dataType === 'delimited') {
			items.push(
				this.fb.group({
					value: new FormControl('', Validators.required)
				})
			);
		} else {
			items.push(
				this.fb.group({
					key: new FormControl('', [Validators.required, this.validateKeyOption]),
					value: new FormControl('', Validators.required)
				})
			);
		}
	}

	removeRow(index: number): void {
		const items = this.form.get('items') as FormArray;
		items.removeAt(index);
		this.emitDataChange(false);
	}

	setArrayInput(array: any[]): void {
		const items = this.form.get('items') as FormArray;
		items.clear();
		array.forEach(item => {
			items.push(
				this.fb.group({
					key: new FormControl(item[this.keyProperty], [Validators.required, this.validateKeyOption]),
					value: new FormControl(item[this.valueProperty], Validators.required)
				})
			);
		});
	}

	setObjectInput(object: any): void {
		const items = this.form.get('items') as FormArray;
		items.clear();
		Object.keys(object).forEach(key => {
			items.push(
				this.fb.group({
					key: new FormControl(key, [Validators.required, this.validateKeyOption]),
					value: new FormControl(object[key], Validators.required)
				})
			);
		});

		if (this.lockKey) {
			items.controls.forEach(control => {
				control.get('key').disable();
			});
		}
	}

	setDelimitedInput(value: string): void {
		const items = this.form.get('items') as FormArray;
		items.clear();
		value.split(this.delimiter).forEach(item => {
			items.push(
				this.fb.group({
					value: new FormControl(item.trim(), Validators.required)
				})
			);
		});
	}

	get items(): FormArray {
		return this.form.get('items') as FormArray;
	}

	onFormValueChanges(): void {
		this.changeSubscription = this.form.valueChanges.pipe(takeUntil(this._unsubscribe)).subscribe(() => {
			this.emitDataChange();
		});
	}

	emitValueChange(): void {
		// Do nothing.
	}

	emitDataChange(setSkipNextChange: boolean = true): void {
		let output;
		const itemsRawValue = this.items.getRawValue();

		if (this.dataType === 'delimited') {
			if (itemsRawValue.length === 0) {
				setSkipNextChange = false;
			}
		}

		if (this.dataType === 'array') {
			output = itemsRawValue.map(item => ({ [this.keyProperty]: item.key, [this.valueProperty]: item.value }));
		} else if (this.dataType === 'object') {
			output = {};
			itemsRawValue.forEach(item => {
				output[item.key] = item.value;
			});
		} else if (this.dataType === 'delimited') {
			output = itemsRawValue.map(item => item.value).join(this.delimiter);
		}

		if (this.parentForm) {
			this.parentForm.get(this.controlName).setValue(output);
		}

		if (setSkipNextChange) {
			this.skipChangeDetection = true;
		}

		// Update JSON for AI suggestions
		this.currentValueJson = JSON.stringify(output || {}, null, 2);

		// console.log('emit: output', output);

		this.valueChange.emit(output);
	}

	clearForm(skipEmit: boolean = false): void {
		const items = this.form.get('items') as FormArray;
		items.clear({ emitEvent: !skipEmit });
	}

	updateFormValue(value: any, i: number) {
		const items = this.form.get('items') as FormArray;
		items.at(i).get('value').setValue(value);
		this.emitDataChange();
	}

	// Build an object with all of the keys and values within to be translated.
	// example: { 'key1': { 'value1': LocaleDto }, 'key2': { 'value2': LocaleDto } }
	updateLocale(key: string, value: any, localeValue: any) {
		let locale = {
			...((this.localeData as any) || {}),
			[key]: {
				...((this.localeData?.[key] as any) || {}),
				[value]: localeValue
			}
		};

		this.localeChange.emit(locale);
	}

	validateKeyOption = (control: AbstractControl) => {
		if (!this.keyOptionsRequired || !this.keyOptions?.length) {
			return null;
		}
		return this.keyOptions.some(opt => opt.value === control.value) ? null : { invalidOption: true };
	};

	/**
	 * Get the current value as a JSON string for AI suggestions
	 */
	getCurrentValueAsJson(): string {
		const itemsRawValue = this.items?.getRawValue() || [];
		let output: any;

		if (this.dataType === 'array') {
			output = itemsRawValue.map(item => ({ [this.keyProperty]: item.key, [this.valueProperty]: item.value }));
		} else if (this.dataType === 'object') {
			output = {};
			itemsRawValue.forEach(item => {
				output[item.key] = item.value;
			});
		} else if (this.dataType === 'delimited') {
			output = itemsRawValue.map(item => item.value).join(this.delimiter);
		}

		return JSON.stringify(output || {}, null, 2);
	}

	/**
	 * Handle AI suggestion applied - parse JSON and update form
	 */
	onAiSuggestionApplied(event: { suggestedCode: string; changes: string[] }): void {
		try {
			const parsed = JSON.parse(event.suggestedCode);

			if (this.dataType === 'object' && typeof parsed === 'object' && !Array.isArray(parsed)) {
				this.setObjectInput(parsed);
				this.emitDataChange();
			} else if (this.dataType === 'array' && Array.isArray(parsed)) {
				this.setArrayInput(parsed);
				this.emitDataChange();
			}
		} catch (e) {
			console.error('Failed to parse AI suggestion as JSON:', e);
		}
	}

	/**
	 * Store original value for undo functionality
	 */
	private originalValueForUndo: string | null = null;

	/**
	 * Handle AI suggestion reverted - restore original value
	 */
	onAiSuggestionReverted(originalCode: string): void {
		try {
			const parsed = JSON.parse(originalCode);

			if (this.dataType === 'object' && typeof parsed === 'object' && !Array.isArray(parsed)) {
				this.setObjectInput(parsed);
				this.emitDataChange();
			} else if (this.dataType === 'array' && Array.isArray(parsed)) {
				this.setArrayInput(parsed);
				this.emitDataChange();
			}
		} catch (e) {
			console.error('Failed to parse reverted code as JSON:', e);
		}
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}
}
